import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Package, ShieldCheck } from 'lucide-react';
import type { OwnerReleaseDto, OwnerReleaseOverviewDto } from '@his/shared';
import { api, get } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, DatePicker, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { isValidTotp, TotpField } from './OwnerConfirm';
import { OWNER_RELEASES_KEY, VERSION_PATTERN } from './OwnerReleasesPage';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export function EditReleasePage() {
  const { releaseId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/owner/releases');

  const overview = useQuery({
    queryKey: OWNER_RELEASES_KEY,
    queryFn: () => get<OwnerReleaseOverviewDto>('/owner/releases'),
    staleTime: 10_000,
    retry: false,
  });
  const release = overview.data?.releases.find((row) => row.id === releaseId) ?? null;

  if (!release) {
    return (
      <FormScreen title="Edit release" icon={Package} backTo={returnTo}>
        <RecordState error={overview.error} missing={Boolean(overview.data)} />
      </FormScreen>
    );
  }

  return <EditReleaseForm key={release.id} release={release} returnTo={returnTo} />;
}

function EditReleaseForm({ release, returnTo }: { release: OwnerReleaseDto; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({
    notes: release.notes ?? '',
    isRequired: release.isRequired,
    minimumAllowedVersion: release.minimumAllowedVersion ?? '',
    requiredBy: release.requiredBy ? release.requiredBy.slice(0, 10) : '',
  }));
  const [notes, setNotes] = useState(initial.notes);
  const [isRequired, setIsRequired] = useState(initial.isRequired);
  const [minimumAllowedVersion, setMinimumAllowedVersion] = useState(initial.minimumAllowedVersion);
  const [requiredBy, setRequiredBy] = useState(initial.requiredBy);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const update = useMutation({
    mutationFn: () =>
      api<OwnerReleaseDto>(`/owner/releases/${encodeURIComponent(release.id)}`, {
        method: 'PATCH',
        body: {
          notes,
          isRequired,
          minimumAllowedVersion: minimumAllowedVersion ? minimumAllowedVersion : null,
          ...(isRequired && requiredBy ? { requiredBy: new Date(`${requiredBy}T23:59:00`).toISOString() } : {}),
          totpCode,
        },
      }),
    onSuccess: () => {
      toast.success('Release updated');
      void queryClient.invalidateQueries({ queryKey: OWNER_RELEASES_KEY });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => {
      setError(describeError(caught));
      toast.error('Could not update the release', describeError(caught));
    },
  });

  const minimumInvalid = minimumAllowedVersion !== '' && !VERSION_PATTERN.test(minimumAllowedVersion);
  const ready = isValidTotp(totpCode) && !minimumInvalid && !(isRequired && !minimumAllowedVersion);
  const dirty =
    !update.isSuccess &&
    (notes !== initial.notes || isRequired !== initial.isRequired || minimumAllowedVersion !== initial.minimumAllowedVersion || requiredBy !== initial.requiredBy || totpCode !== '');

  return (
    <FormScreen
      title={`Edit release ${release.version}`}
      description="Notes, the required flag and the minimum allowed version. The package file itself never changes."
      icon={Package}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        update.mutate();
      }}
    >
      <FormSection title="Release notes" icon={FileText} columns={1}>
        <Field label="Release notes" htmlFor="edit-notes">
          <Textarea id="edit-notes" rows={6} className="font-mono text-xs" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection title="Requirement" icon={Package}>
        <Checkbox className="sm:col-span-2" checked={isRequired} onCheckedChange={setIsRequired} label="Required" />
        {isRequired && (
          <Field
            label="Required by"
            htmlFor="edit-required-by"
            hint="Leave empty for 14 days from when it was marked required. On that date each server installs it at 02:00; it goes read-only only if that fails, at the next 09:00."
          >
            <DatePicker id="edit-required-by" value={requiredBy} onChange={(value) => setRequiredBy(value)} />
          </Field>
        )}
        <Field label="Minimum allowed version" htmlFor="edit-minimum" hint={minimumInvalid ? 'Use the form 1.4.0 or 1.4.0-beta.1' : undefined}>
          <Input id="edit-minimum" className="font-mono" value={minimumAllowedVersion} onChange={(e) => setMinimumAllowedVersion(e.target.value.trim())} />
        </Field>
      </FormSection>

      <FormSection title="Confirm" icon={ShieldCheck} columns={1}>
        <TotpField value={totpCode} onChange={setTotpCode} />
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions submitLabel="Save changes" loading={update.isPending} incomplete={!ready} cancelTo={returnTo} bleed={false} />
    </FormScreen>
  );
}
